import { can } from '@simplificalo/contracts/permissions';
import { createFileRoute } from '@tanstack/react-router';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Clock,
  Lock,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useLaborParams,
  useLaborParamHistory,
  useCreateLaborParam,
} from '@/features/rrhh/hooks/use-labor-params';
import type {
  LaborParamCurrent,
  LaborParamHistoryEntry,
  LaborParamUnit,
} from '@/features/rrhh/schemas/types';
import { useCurrentUserRole } from '@/lib/team';

export const Route = createFileRoute('/_app/settings/labor-params')({
  component: LaborParamsPage,
});

// ─── Labels legibles para cada key ────────────────────────────────

const PARAM_LABELS: Record<string, string> = {
  imm: 'Ingreso Mínimo Mensual',
  imm_menores: 'IMM menores de 18 y mayores de 65',
  imm_no_remuneracional: 'IMM no remuneracional',
  utm: 'Unidad Tributaria Mensual',
  tope_imponible_afp: 'Tope imponible AFP',
  tope_imponible_ips: 'Tope imponible IPS',
  tope_seguro_cesantia: 'Tope seguro cesantía',
  tasa_sis: 'Seguro Invalidez y Sobrevivencia (SIS)',
  tasa_salud_base: 'Cotización salud base',
  tasa_ccaf: 'CCAF',
  tasa_mutual_base: 'Mutual base',
  seguro_cesantia_indef_emp: 'Seguro cesantía indefinido (empleador)',
  seguro_cesantia_indef_trab: 'Seguro cesantía indefinido (trabajador)',
  seguro_cesantia_pf_emp: 'Seguro cesantía plazo fijo (empleador)',
  tasa_afp_capital: 'AFP Capital',
  tasa_afp_cuprum: 'AFP Cuprum',
  tasa_afp_habitat: 'AFP Habitat',
  tasa_afp_modelo: 'AFP Modelo',
  tasa_afp_planvital: 'AFP PlanVital',
  tasa_afp_provida: 'AFP ProVida',
  tasa_afp_uno: 'AFP Uno',
  jornada_maxima_semanal: 'Jornada máxima semanal',
  jornada_parcial_maxima: 'Jornada parcial máxima',
  dias_vacaciones_estandar: 'Días vacaciones estándar',
  dias_vacaciones_zona_extrema: 'Días vacaciones zona extrema',
  factor_vacaciones_corridos: 'Factor vacaciones corridos',
  tope_anos_indemnizacion: 'Tope años indemnización',
};

function getLabel(key: string): string {
  return PARAM_LABELS[key] ?? key;
}

const UNIT_LABELS: Record<LaborParamUnit, string> = {
  clp: 'CLP',
  pct: '%',
  hrs: 'hrs',
  days: 'días',
  ratio: 'ratio',
};

const SOURCE_LABELS: Record<string, string> = {
  seed: 'Semilla',
  previred: 'Previred',
  manual: 'Manual',
};

function formatValue(value: number, unit: LaborParamUnit): string {
  if (unit === 'clp') return `$${value.toLocaleString('es-CL')}`;
  if (unit === 'pct') return `${value}%`;
  return `${value} ${UNIT_LABELS[unit]}`;
}

function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

// ─── Page ──────────────────────────────────────────────────────────

function LaborParamsPage() {
  const role = useCurrentUserRole();
  const canEdit = can(role, 'rrhh.admin');
  const { data, isLoading, error } = useLaborParams();
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  if (!canEdit) {
    return (
      <div className="space-y-4">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Parámetros laborales</h1>
          <p className="mt-1 text-sm text-[color:var(--color-fg-muted)]">
            <span className="inline-flex items-center gap-1 rounded-md bg-[color:var(--color-bg-overlay)] px-1.5 py-0.5 text-xs">
              <Lock className="h-3 w-3" aria-hidden /> Sin acceso — requiere permiso rrhh.admin
            </span>
          </p>
        </header>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Parámetros laborales</h1>
          <p className="mt-1 text-sm text-[color:var(--color-fg-muted)]">
            Valores legales nacionales usados en cálculos de finiquito, costo empleador y
            validaciones. Los parámetros marcados con{' '}
            <span className="inline-flex items-center gap-0.5 rounded bg-blue-500/10 px-1 py-0.5 text-xs text-blue-600 dark:text-blue-400">
              <RefreshCw className="h-3 w-3" /> Previred
            </span>{' '}
            se actualizan automáticamente vía cron.
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} size="sm">
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          Nuevo valor
        </Button>
      </header>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : error ? (
        <div
          className="rounded-lg border px-3 py-2 text-xs"
          style={{
            background: 'color-mix(in srgb, var(--color-danger, #ef4444) 8%, transparent)',
            borderColor: 'color-mix(in srgb, var(--color-danger, #ef4444) 30%, transparent)',
          }}
        >
          Error al cargar parámetros: {error.message}
        </div>
      ) : (
        <ParamsTable
          params={data?.data ?? []}
          expandedKey={expandedKey}
          onToggle={(key) => setExpandedKey(expandedKey === key ? null : key)}
        />
      )}

      <CreateParamDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />
    </div>
  );
}

// ─── Table ─────────────────────────────────────────────────────────

interface ParamsTableProps {
  readonly params: LaborParamCurrent[];
  readonly expandedKey: string | null;
  readonly onToggle: (key: string) => void;
}

function ParamsTable({ params, expandedKey, onToggle }: ParamsTableProps) {
  if (params.length === 0) {
    return (
      <div
        className="rounded-lg border p-6 text-center text-sm text-[color:var(--color-fg-muted)]"
        style={{ borderColor: 'var(--color-border)' }}
      >
        No hay parámetros cargados. Ejecuta la migración 0110 o el cron de Previred.
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-lg border"
      style={{ borderColor: 'var(--color-border)' }}
    >
      <table className="w-full text-sm">
        <thead>
          <tr
            className="border-b text-left text-xs font-medium text-[color:var(--color-fg-muted)]"
            style={{ borderColor: 'var(--color-border)', background: 'var(--color-bg-elevated)' }}
          >
            <th className="w-8 px-3 py-2" />
            <th className="px-3 py-2">Parámetro</th>
            <th className="px-3 py-2 text-right">Valor vigente</th>
            <th className="px-3 py-2">Vigente desde</th>
            <th className="px-3 py-2">Fuente</th>
          </tr>
        </thead>
        <tbody>
          {params.map((p) => (
            <ParamRow
              key={p.key}
              param={p}
              isExpanded={expandedKey === p.key}
              onToggle={() => onToggle(p.key)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Row ───────────────────────────────────────────────────────────

interface ParamRowProps {
  readonly param: LaborParamCurrent;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
}

function ParamRow({ param, isExpanded, onToggle }: ParamRowProps) {
  const isPrevired = param.source === 'previred';

  return (
    <>
      <tr
        className="cursor-pointer border-b transition-colors hover:bg-[color:var(--color-bg-overlay)]"
        style={{ borderColor: 'var(--color-border)' }}
        onClick={onToggle}
      >
        <td className="px-3 py-2.5 text-[color:var(--color-fg-subtle)]">
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </td>
        <td className="px-3 py-2.5">
          <div className="font-medium text-[color:var(--color-fg)]">{getLabel(param.key)}</div>
          <div className="text-xs text-[color:var(--color-fg-subtle)]">{param.key}</div>
        </td>
        <td className="px-3 py-2.5 text-right font-mono text-[color:var(--color-fg)]">
          {formatValue(param.value, param.unit)}
        </td>
        <td className="px-3 py-2.5 text-[color:var(--color-fg-muted)]">
          {formatDate(param.validFrom)}
        </td>
        <td className="px-3 py-2.5">
          <SourceBadge source={param.source} />
        </td>
      </tr>
      {isExpanded ? (
        <tr>
          <td colSpan={5} className="bg-[color:var(--color-bg-elevated)] p-0">
            <HistoryPanel paramKey={param.key} isPrevired={isPrevired} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

// ─── Source badge ──────────────────────────────────────────────────

function SourceBadge({ source }: { readonly source: string }) {
  if (source === 'previred') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-600 dark:text-blue-400">
        <RefreshCw className="h-3 w-3" aria-hidden />
        Previred
      </span>
    );
  }
  if (source === 'manual') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-600 dark:text-green-400">
        Manual
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-500/10 px-2 py-0.5 text-xs font-medium text-[color:var(--color-fg-muted)]">
      {SOURCE_LABELS[source] ?? source}
    </span>
  );
}

// ─── History panel ─────────────────────────────────────────────────

function HistoryPanel({
  paramKey,
  isPrevired,
}: {
  readonly paramKey: string;
  readonly isPrevired: boolean;
}) {
  const { data, isLoading, error } = useLaborParamHistory(paramKey);

  return (
    <div className="border-t px-6 py-4" style={{ borderColor: 'var(--color-border)' }}>
      {isPrevired ? (
        <div className="mb-3 flex items-start gap-2 rounded-md bg-blue-500/5 px-3 py-2 text-xs text-blue-700 dark:text-blue-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            Este parámetro se actualiza automáticamente desde Previred. Los valores manuales serán
            sobrescritos en la próxima ejecución del cron.
          </span>
        </div>
      ) : null}

      <h4 className="mb-2 text-xs font-semibold text-[color:var(--color-fg-muted)]">
        Historial de valores
      </h4>

      {isLoading ? (
        <div className="space-y-1.5">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
      ) : error ? (
        <div className="text-xs text-red-500">Error: {error.message}</div>
      ) : !data?.data.length ? (
        <div className="text-xs text-[color:var(--color-fg-subtle)]">Sin historial</div>
      ) : (
        <div className="space-y-0">
          {data.data.map((entry, i) => (
            <HistoryRow key={entry.id} entry={entry} isCurrent={i === 0} />
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryRow({
  entry,
  isCurrent,
}: {
  readonly entry: LaborParamHistoryEntry;
  readonly isCurrent: boolean;
}) {
  return (
    <div
      className="flex items-center gap-4 border-b px-2 py-1.5 text-xs last:border-b-0"
      style={{ borderColor: 'var(--color-border)' }}
    >
      <div className="w-28 font-mono text-[color:var(--color-fg)]">
        {formatValue(entry.value, entry.unit)}
      </div>
      <div className="flex items-center gap-1.5 text-[color:var(--color-fg-muted)]">
        <Clock className="h-3 w-3" aria-hidden />
        {formatDate(entry.validFrom)}
      </div>
      <SourceBadge source={entry.source} />
      {isCurrent ? (
        <span className="rounded-full bg-green-500/10 px-1.5 py-0.5 text-[10px] font-medium text-green-600 dark:text-green-400">
          Vigente
        </span>
      ) : null}
      <div className="ml-auto text-[color:var(--color-fg-subtle)]">
        {new Date(entry.createdAt).toLocaleDateString('es-CL')}
      </div>
    </div>
  );
}

// ─── Create dialog ─────────────────────────────────────────────────

function CreateParamDialog({
  open,
  onOpenChange,
}: {
  readonly open: boolean;
  readonly onOpenChange: (v: boolean) => void;
}) {
  const create = useCreateLaborParam();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState<LaborParamUnit>('clp');
  const [validFrom, setValidFrom] = useState('');

  const reset = () => {
    setKey('');
    setValue('');
    setUnit('clp');
    setValidFrom('');
  };

  const handle = (e: { preventDefault(): void }) => {
    e.preventDefault();
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      toast.error('Valor debe ser numérico');
      return;
    }
    create.mutate(
      { key: key.trim(), value: numValue, unit, validFrom },
      {
        onSuccess: () => {
          toast.success('Parámetro creado');
          reset();
          onOpenChange(false);
        },
        onError: (err) => {
          const msg =
            'friendlyMessage' in err
              ? (err as { friendlyMessage: string }).friendlyMessage
              : err.message;
          toast.error(`Error: ${msg}`);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuevo valor de parámetro</DialogTitle>
          <DialogDescription>
            Agrega un valor manual para un parámetro laboral. Se usará en cálculos cuya fecha de
            referencia sea igual o posterior a la fecha de vigencia.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handle} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="lp-key">Clave del parámetro</Label>
            <Input
              id="lp-key"
              required
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="imm, tasa_mutual_base, ..."
            />
            <p className="text-[10px] text-[color:var(--color-fg-subtle)]">
              snake_case. Si ya existe, se agrega como nuevo valor histórico.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="lp-value">Valor</Label>
              <Input
                id="lp-value"
                required
                type="number"
                step="any"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="553553"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lp-unit">Unidad</Label>
              <select
                id="lp-unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value as LaborParamUnit)}
                className="flex h-9 w-full rounded-md border px-3 py-1 text-sm"
                style={{
                  background: 'var(--color-bg)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-fg)',
                }}
              >
                <option value="clp">CLP (pesos)</option>
                <option value="pct">% (porcentaje)</option>
                <option value="hrs">Horas</option>
                <option value="days">Días</option>
                <option value="ratio">Ratio</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="lp-valid-from">Fecha de vigencia</Label>
            <Input
              id="lp-valid-from"
              required
              type="date"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
            />
            <p className="text-[10px] text-[color:var(--color-fg-subtle)]">
              Primer día en que el valor es válido. Para mensuales, usar el 1ro del mes.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
